import { useCallback, useMemo } from "react";
import { format } from "date-fns";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RangeDateField from "../../../../components/Form/RangeDateField";
import { PILL_TRIGGER_VARIANT } from "../../../../components/Buttons/PillTriggerButton";
import { useTranslationApp } from "../../../../lib/i18next";
import { AD_SPEND_DATE_PRESET } from "../../../../utils/constants";
import { getDateFnsLocale } from "../../../../utils/i18nLanguage";
import {
  OWNER_AD_SPEND_PRESET_CUSTOM,
  OWNER_AD_SPEND_PRESET_HISTORIC,
  OWNER_AD_SPEND_PRESET_LAST_7,
  OWNER_AD_SPEND_PRESET_LAST_30,
  OWNER_AD_SPEND_PRESET_LAST_90,
  OWNER_AD_SPEND_PRESET_LAST_MONTH,
  OWNER_AD_SPEND_PRESET_THIS_MONTH,
} from "../../../../i18n/keysTranslations";

const PRESETS = [
  {
    id: AD_SPEND_DATE_PRESET.historic,
    labelKey: OWNER_AD_SPEND_PRESET_HISTORIC,
  },
  { id: AD_SPEND_DATE_PRESET.last_7, labelKey: OWNER_AD_SPEND_PRESET_LAST_7 },
  { id: AD_SPEND_DATE_PRESET.last_30, labelKey: OWNER_AD_SPEND_PRESET_LAST_30 },
  { id: AD_SPEND_DATE_PRESET.last_90, labelKey: OWNER_AD_SPEND_PRESET_LAST_90 },
  {
    id: AD_SPEND_DATE_PRESET.this_month,
    labelKey: OWNER_AD_SPEND_PRESET_THIS_MONTH,
  },
  {
    id: AD_SPEND_DATE_PRESET.last_month,
    labelKey: OWNER_AD_SPEND_PRESET_LAST_MONTH,
  },
  { id: AD_SPEND_DATE_PRESET.custom, labelKey: OWNER_AD_SPEND_PRESET_CUSTOM },
];

const RANGE_DATE_FORMAT = "d MMM yyyy";

const ACTIVE_CHIP_SX = {
  bgcolor: "common.black",
  color: "common.white",
  borderColor: "common.black",
  "& .MuiChip-label": { color: "common.white" },
  "&:hover, &.MuiChip-clickable:hover": { bgcolor: "grey.800" },
};

const INACTIVE_CHIP_SX = {
  color: "text.primary",
  "& .MuiChip-label": { color: "text.primary" },
};

const AdSpendFilters = ({ preset, onChangePreset = () => {}, control }) => {
  const { t, i18n } = useTranslationApp();
  const language = i18n.language;
  const maxDate = useMemo(() => new Date(), []);
  const showCustomRange = preset === AD_SPEND_DATE_PRESET.custom;

  const onFormatRangeText = useCallback(
    ({ startDateTime, endDateTime }) => {
      const locale = getDateFnsLocale(language);
      const from = format(startDateTime, RANGE_DATE_FORMAT, { locale });
      const to = format(endDateTime, RANGE_DATE_FORMAT, { locale });
      return `${from} – ${to}`;
    },
    [language]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
      }}
    >
      {PRESETS.map((item) => {
        const isActive = preset === item.id;
        return (
          <Chip
            key={item.id}
            label={t(item.labelKey)}
            size="small"
            clickable
            variant={isActive ? "filled" : "outlined"}
            aria-pressed={isActive}
            sx={isActive ? ACTIVE_CHIP_SX : INACTIVE_CHIP_SX}
            onClick={() => onChangePreset(item.id)}
          />
        );
      })}
      {showCustomRange ? (
        <RangeDateField
          mode="text"
          triggerVariant={PILL_TRIGGER_VARIANT.PILL}
          startIcon={
            <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          }
          maxDate={maxDate}
          control={control}
          name="dateRange"
          showStartNowButton={false}
          showIconText={true}
          showPresets={false}
          formatRangeText={onFormatRangeText}
          onSave={() => onChangePreset(AD_SPEND_DATE_PRESET.custom)}
        />
      ) : null}
    </Box>
  );
};

export default AdSpendFilters;
